import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Circle,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix para o ícone padrão do Leaflet não aparecer
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const StyledMapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #d1d5db;
  
  .leaflet-container {
    width: 100%;
    height: 100%;
    border-radius: 8px;
  }
  
  .leaflet-control-attribution {
    font-size: 10px;
  }
`;

const RadiusToolPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 180px minmax(260px, auto);
  gap: 10px;
  align-items: stretch;
  margin-bottom: 10px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const OriginInput = styled.input`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #1F1F1F;
  font-family: inherit;
  font-size: 14px;
  padding: 12px 14px;
  background: #ffffff;
  box-sizing: border-box;
`;

const RadiusSummary = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
`;

const CoordinatesDisplay = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;
  
  .material-icons {
    font-size: 18px;
  }
`;

const AdjustButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 8px 16px;
  background: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #1264C7;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  z-index: 20;
  
  &:hover {
    background: #f3f4f6;
    color: #1264C7;
  }
  
  .material-icons {
    font-size: 16px;
  }
`;

const RadiusSelect = styled.select`
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #1F1F1F;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 8px;
  background: #ffffff;
`;

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange: (lat: number, lng: number) => void;
  onRadiusChange?: (radiusKm: number) => void;
  radiusKm?: number;
  startAddress?: string;
}

// Componente para lidar com eventos do mapa
const DraggableMarker: React.FC<{
  position: [number, number];
  onPositionChange: (lat: number, lng: number) => void;
}> = ({ position, onPositionChange }) => {
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(position);

  useEffect(() => {
    setMarkerPosition(position);
  }, [position]);

  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setMarkerPosition([lat, lng]);
      onPositionChange(lat, lng);
    },
  });

  return (
    <Marker
      position={markerPosition}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target;
          const position = marker.getLatLng();
          setMarkerPosition([position.lat, position.lng]);
          onPositionChange(position.lat, position.lng);
        },
      }}
    />
  );
};

const MapViewUpdater: React.FC<{
  position: [number, number];
  radiusKm: number;
}> = ({ position, radiusKm }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(position, radiusKm > 10 ? 11 : 12);
  }, [map, position, radiusKm]);

  return null;
};

export const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude = -14.235,
  longitude = -51.9253,
  onLocationChange,
  onRadiusChange,
  radiusKm = 15,
  startAddress,
}) => {
  const [position, setPosition] = useState<[number, number]>([latitude, longitude]);
  const radiusMeters = radiusKm * 1000;
  const circleAreaKm2 = Math.PI * radiusKm * radiusKm;
  const originLabel =
    startAddress || `Lat/Lon: ${position[0].toFixed(6)}, ${position[1].toFixed(6)}`;

  useEffect(() => {
    setPosition([latitude, longitude]);
  }, [latitude, longitude]);

  const handlePositionChange = (lat: number, lng: number) => {
    setPosition([lat, lng]);
    onLocationChange(lat, lng);
  };

  const handleAdjust = () => {
    // Centralizar no Brasil (ou outra localização padrão)
    const defaultLat = -14.235;
    const defaultLng = -51.9253;
    setPosition([defaultLat, defaultLng]);
    onLocationChange(defaultLat, defaultLng);
  };

  return (
    <>
      <RadiusToolPanel>
        <OriginInput
          aria-label="Ponto de partida"
          readOnly
          value={originLabel}
        />
        <RadiusSelect
          aria-label="Raio de alcance no mapa"
          value={radiusKm}
          onChange={(event) => onRadiusChange?.(Number(event.target.value))}
        >
          <option value={2}>Raio 2 km</option>
          <option value={5}>Raio 5 km</option>
          <option value={10}>Raio 10 km</option>
          <option value={15}>Raio 15 km</option>
        </RadiusSelect>
        <RadiusSummary>
          <span>
            Raio: {radiusMeters.toLocaleString("pt-BR")} m |{" "}
            {radiusKm.toLocaleString("pt-BR", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}{" "}
            km
          </span>
          <span>
            Area do circulo:{" "}
            {circleAreaKm2.toLocaleString("pt-BR", {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}{" "}
            km²
          </span>
        </RadiusSummary>
      </RadiusToolPanel>

      <StyledMapContainer>
        <MapContainer
          center={position}
          zoom={radiusKm > 10 ? 11 : 12}
          style={{ height: '100%', width: '100%' }}
        >
          <MapViewUpdater position={position} radiusKm={radiusKm} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Circle
            center={position}
            pathOptions={{
              color: '#1264C7',
              fillColor: '#1264C7',
              fillOpacity: 0.12,
              weight: 2,
            }}
            radius={radiusMeters}
          />
          <DraggableMarker position={position} onPositionChange={handlePositionChange} />
        </MapContainer>

        <AdjustButton onClick={handleAdjust}>
          <span className="material-icons">my_location</span>
          Centralizar no Brasil
        </AdjustButton>
      </StyledMapContainer>

      <CoordinatesDisplay>
        <span className="material-icons">location_on</span>
        <span>
          Coordenadas: {position[0].toFixed(6)}, {position[1].toFixed(6)}
        </span>
        <span>Raio de alcance: {radiusKm} km</span>
      </CoordinatesDisplay>
    </>
  );
};
